import Api from "../../services/Api.ts";
import {useEffect, useState} from "react";
import {useAtom, useSetAtom} from 'jotai';
import {categoriesAtom, matchingClientsAtom, selectedDepartmentsAtom} from '../../jotai_state';
import TooFewClients from "../../components/TooFewClients/TooFewClients.tsx";
import PercentBar from "../../components/Bars/PercentBar.tsx";
import RiskBadge from "../../components/Badges/RiskBadge.tsx";
import {ChevronDown} from "flowbite-react-icons/outline";
import ValueBar from "../../components/Bars/ValueBar.tsx";

interface QuestionInterface {
  id: string
  name: string;
  score: number;
  reversed: boolean;
}

interface CategoryInterface {
  id: string;
  name: string;
  score: number;
  count: number;
  questions: Array<QuestionInterface>;
}

interface DriversInterface {
  name: string;
  avgScore: number;
  driversNr: number
  riskCategory: Array<number>;
  count: number;
  categories: Array<CategoryInterface>;
}

export default function SprintProfile() {
  const api = Api.getInstance();
  const [tooFewClients, setTooFewClients] = useState<number|undefined>(undefined);
  const [drivers, setDrivers] = useState<Array<DriversInterface>>([]);
  const [categories] = useAtom(categoriesAtom);
  const [selectedDepartments] = useAtom(selectedDepartmentsAtom);
  const setMatchingClients = useSetAtom(matchingClientsAtom);
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState("");
  const [openedSections, setOpenedSections] = useState<Array<string>>([]);
  const zoneLimits = [4, 6]

  useEffect(() => {
    getData()
  }, [categories, selectedDepartments]);

  const getData = () => {
    const data = {
      categories: categories,
      departments: selectedDepartments,
    }

    api.post('customer/sprint_drivers', data).then((response) => {
      if (response.json.data.tooFewClients !== undefined) {
        setTooFewClients(response.json.data.tooFewClients);
      } else {
        setDrivers(response.json.data.drivers);
        setMatchingClients(response.json.data.matchingClients);
        setTooFewClients(undefined);
      }
      setLoaded(true);
    }).catch(error => {
      setError('Unknown error (http status ' + error.response.status + ')')
      setLoaded(true)
    })
  }

  const toggleSection = (sectionId: string, isOpened: boolean)  => {
    if (!isOpened) {
      setOpenedSections([...openedSections, sectionId]);
    } else {
      const _openedSections: Array<string> = [];
      for (const _sectionId of openedSections) {
        if (sectionId !== _sectionId) {
          _openedSections.push(_sectionId);
        }
      }
      setOpenedSections(_openedSections);
    }
  }

  const renderMain = () => {

    if (!loaded) {
      return <div>Loading data...</div>
    }
    if (error.length > 0) {
      return <div>{error}</div>
    }
    if (!drivers || drivers.length === 0) {
      return <div>No drivers found</div>
    }
    if (tooFewClients !== undefined) {
      return <TooFewClients tooFewClients={tooFewClients}/>
    }

    const getScoreColor = (score: number, max: number) => {
      if (score < zoneLimits[0]) {
        return "cherry-600";
      } else if (score < zoneLimits[1]) {
        return "orange-500";
      } else {
        return "aquamarine-600";
      }
    }

    const renderQuestions = (category: CategoryInterface) => {
      return (
        <div className="z-box mt-2">
          <div className="z-title2">Questions</div>
          {
            category.questions.map(question => {
              const scoreColor = getScoreColor(question.score, 7);
              return (
                <div key={"q_" + question.id}>
                  <div className="mt-2 flex justify-between">
                    <div className="z-body1">{question.reversed ? "* " : ""}{question.name}</div>
                    <div className="flex justify-end">
                      <div className="z-body1">{question.score.toFixed(1)}</div>
                    </div>
                  </div>

                  <ValueBar color={scoreColor} value={question.score} max={7}/>
                </div>
              )
            })
          }
        </div>
      )
    }

    return (
      <>
        <div className="z-h2">Your organisational states</div>
        <div className="z-body1 mt-4 text-gray-1000!">
          How the initiative itself is assessed: where it is strong, where there are gaps, and how aligned or
          scattered the answers are across units.
        </div>
        {
          drivers.map((driver, i) => {
            return (
              <div key={"driver_" + i} className="mb-4">
                <div className="z-h3 mt-4">{driver.name}</div>
                <div className="mt-4 z-body2">7 scale, {driver.count} answers</div>
                <div className="flex gap-x-8">
                  <div><span className="text-red">Critical:</span> &lt; 3</div>
                  <div><span className="text-orange">Risk:</span> 3-6</div>
                  <div><span className="text-green">Strength:</span> &gt; 6</div>
                </div>

                <div className="flex gap-x-4 mt-8">
                  <div className="z-box w-1/2">
                    <div className="z-title4 text-gray-800!">Avg. State score</div>
                    <div className="z-h1">{driver.avgScore.toFixed(1)}</div>
                    <div className="z-body1 text-gray-800!">Across all {driver.driversNr} states</div>
                  </div>
                  <div className="z-box w-1/2">
                    <div className="z-title4 text-gray-800!">Response scale</div>
                    <div className="body1 text-indigo-1000! mt-4">
                      7-point scale
                    </div>

                    <div className="mt-1 z-title3 text-cherry-600!">Critical: &lt; {zoneLimits[0]}</div>
                    <div className="z-title3 text-orange-500!">Risk: {zoneLimits[0]} - {zoneLimits[1]}</div>
                    <div className="flex items-center justify-between">
                      <div className="z-title3 text-aquamarine-600!">Strength: &gt; {zoneLimits[1]}</div>
                      <div className="z-title4 text-gray-600!">*=Reversed scale</div>
                    </div>

                  </div>
                </div>

                <div className="mt-4">
                  <PercentBar values={driver.riskCategory} texts={[" Critical", " Risk", " Strength"]} height="32px"
                              width="100%" goodLeft={false}/>
                </div>

                <div className="mt-8 flex justify-between border-b border-neutral-300 pb-2">
                  <div className="z-title3 text-gray-800!">State</div>
                  <div className="flex">
                  <div className="z-title3 w-20 text-center text-gray-800!">Score</div>
                    <div className="z-title3 w-16 text-center text-gray-800!">Zone</div>
                    <div className="w-6"/>
                  </div>
                </div>

                {
                  driver.categories.map(category => {
                    const isOpened = openedSections.indexOf(category.id) > -1;
                    return (
                      <div key={"cat_" + category.id} className="cursor-pointer border-b border-neutral-300"
                           onClick={() => toggleSection(category.id, isOpened)}>
                        <div className="h-10 flex items-center justify-between">
                          <div className="z-title3">{category.name}</div>
                          <div className="flex">
                            <div className="z-title3 w-20 text-center">{category.score.toFixed(1)}</div>
                            <div className="z-title3 w-16 text-center">
                              <RiskBadge score={category.score} zoneLimits={zoneLimits}/>
                            </div>
                            <div className="w-6">
                              <ChevronDown className={"ml-3 h-4 w-4 " + (isOpened ? "rotate-180" : "")}/>
                            </div>
                          </div>
                        </div>

                        {isOpened && renderQuestions(category)}

                      </div>
                    )
                  })
                }
              </div>
            )
          })
        }

      </>
    )
  }

  return (
    <>
    {renderMain()}
    </>
  );
}